<script setup lang="ts">
import {useEventListener} from "@vueuse/core";
import {ChevronLeft, ChevronRight, FileText, Printer, X} from "@lucide/vue";
import MedicationCheckupPrescriptionPage from "@/medication-checkup/components/MedicationCheckupPrescriptionPage.vue";
import TreatmentSheets, {TreatmentSheet} from "@/medication-checkup/services/TreatmentSheets";
import {useSheetCursor} from "@/medication-checkup/composables/useSheetCursor";
import fileHelper from "@/core/helpers/fileHelper";

/**
 * The prescriptions of the collection full screen, one at a time, as large
 * as the window allows: ← and → go from one to the next, Escape closes.
 */

const props = defineProps<{
    sheets: TreatmentSheet[];
    /**
     * The sheet opened first.
     */
    start: number;
}>();

const emit = defineEmits<{close: []}>();

const {index, sheet, go} = useSheetCursor(() => props.sheets, props.start);

const print = () => {
    if (sheet.value?.prescriptionKey) {
        fileHelper.printPdf(TreatmentSheets.urls(sheet.value.prescriptionKey).pdf);
    }
};

useEventListener(window, 'keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
        event.stopPropagation();
        emit('close');
    } else if (event.key === 'ArrowRight') {
        go(1);
    } else if (event.key === 'ArrowLeft') {
        go(-1);
    }
});
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-[2000] flex flex-col items-center gap-3 bg-[rgba(19,20,45,.84)] px-6 pb-3.5 pt-[18px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="medication-checkup-prescription-viewer-title"
      @click="emit('close')"
    >
      <div class="flex items-center gap-3 self-stretch text-white" @click.stop>
        <FileText class="size-5" />
        <span class="flex flex-col">
          <span id="medication-checkup-prescription-viewer-title" class="text-[15px] font-semibold">
            {{ sheet?.prescriber ?? 'Prescripteur inconnu' }}
          </span>
          <span v-if="sheet?.deliveredAt" class="text-[12px] opacity-80">
            Délivrée le {{ $filters.date(sheet.deliveredAt, 'dayOnly') }}
          </span>
        </span>
        <span class="flex-1" />
        <span v-if="sheets.length > 1" class="text-[12px] opacity-80">{{ index + 1 }} / {{ sheets.length }}</span>
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-full border-[1.5px] border-solid border-white/60 px-3.5 py-1.5 text-[12.5px] font-semibold !text-white"
          @click="print"
        >
          <Printer class="size-4" />
          Imprimer
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-full border-[1.5px] border-solid border-white/60 px-3.5 py-1.5 text-[12.5px] font-semibold !text-white"
          @click="emit('close')"
        >
          <X class="size-4" />
          Fermer
        </button>
      </div>

      <div class="flex min-h-0 flex-1 items-center gap-5">
        <button
          type="button"
          class="flex size-11 shrink-0 items-center justify-center rounded-full bg-white/15 text-white disabled:opacity-35"
          :disabled="index === 0"
          title="Ordonnance précédente"
          @click.stop="go(-1)"
        >
          <ChevronLeft class="size-6" />
        </button>

        <div
          class="h-full aspect-[480/678] overflow-hidden rounded-[3px] bg-white shadow-[0_10px_40px_rgba(0,0,0,.4)]"
          @click.stop
        >
          <MedicationCheckupPrescriptionPage
            v-if="sheet?.prescriptionKey"
            :key="sheet.prescriptionKey"
            :prescription-key="sheet.prescriptionKey"
          />
        </div>

        <button
          type="button"
          class="flex size-11 shrink-0 items-center justify-center rounded-full bg-white/15 text-white disabled:opacity-35"
          :disabled="index === sheets.length - 1"
          title="Ordonnance suivante"
          @click.stop="go(1)"
        >
          <ChevronRight class="size-6" />
        </button>
      </div>

      <span class="text-[11.5px] text-white/75">← → pour changer d'ordonnance · Échap pour fermer</span>
    </div>
  </Teleport>
</template>
